import type { OUItem } from '@/api/types';

export interface OuNode {
  ou: OUItem;
  children: OuNode[];
}

/**
 * Agrupa el catálogo plano de OUs (ligado por parentOuId) en un árbol real.
 * Una OU cuyo parentOuId no existe en el catálogo (huérfana) se trata como raíz.
 */
export function buildOuTree(items: OUItem[]): OuNode[] {
  const knownIds = new Set(items.map(o => o.id));
  const childrenByParent = new Map<string, OUItem[]>();
  const roots: OUItem[] = [];

  for (const ou of items) {
    const parentId = ou.parentOuId && knownIds.has(ou.parentOuId) ? ou.parentOuId : null;
    if (parentId) {
      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(ou);
      childrenByParent.set(parentId, siblings);
    } else {
      roots.push(ou);
    }
  }

  function build(ou: OUItem): OuNode {
    return { ou, children: (childrenByParent.get(ou.id) ?? []).map(build) };
  }

  return roots.map(build);
}

/** IDs de todos los nodos (en cualquier nivel) que tienen al menos un hijo. */
export function collectIdsWithChildren(nodes: OuNode[]): Set<string> {
  const ids = new Set<string>();
  function visit(node: OuNode) {
    if (node.children.length > 0) {
      ids.add(node.ou.id);
      node.children.forEach(visit);
    }
  }
  nodes.forEach(visit);
  return ids;
}

function matchesQuery(ou: OUItem, query: string): boolean {
  return ou.name.toLowerCase().includes(query) || (ou.description ?? '').toLowerCase().includes(query);
}

/** IDs de nodos (en cualquier nivel) cuyo nombre/descripción hace match directo con `query` (ya en minúsculas). */
export function collectMatchIds(nodes: OuNode[], query: string): Set<string> {
  const ids = new Set<string>();
  function visit(node: OuNode) {
    if (matchesQuery(node.ou, query)) ids.add(node.ou.id);
    node.children.forEach(visit);
  }
  nodes.forEach(visit);
  return ids;
}

/** IDs de nodos que deben forzarse a expandir para que un match en su subárbol quede visible. */
export function collectAncestorIdsOfMatches(nodes: OuNode[], matchIds: Set<string>): Set<string> {
  const ancestors = new Set<string>();
  function visit(node: OuNode): boolean {
    const childHasMatch = node.children.map(visit).some(Boolean);
    if (childHasMatch) ancestors.add(node.ou.id);
    return childHasMatch || matchIds.has(node.ou.id);
  }
  nodes.forEach(visit);
  return ancestors;
}

/** Raíces cuyo subárbol (incluyéndose a sí mismas) contiene al menos un match. */
export function rootsWithMatches(nodes: OuNode[], matchIds: Set<string>): OuNode[] {
  function subtreeHasMatch(node: OuNode): boolean {
    if (matchIds.has(node.ou.id)) return true;
    return node.children.some(subtreeHasMatch);
  }
  return nodes.filter(subtreeHasMatch);
}

/** Camino desde la raíz hasta el nodo con `targetId`, inclusive (último elemento = el nodo buscado). null si no existe. */
export function findOuNodePath(nodes: OuNode[], targetId: string): OuNode[] | null {
  for (const node of nodes) {
    if (node.ou.id === targetId) return [node];
    const childPath = findOuNodePath(node.children, targetId);
    if (childPath) return [node, ...childPath];
  }
  return null;
}

/** IDs de todos los nodos de `node` y su subárbol completo, incluyéndose a sí mismo. */
export function collectSubtreeIds(node: OuNode): string[] {
  const subtreeIds = [node.ou.id];
  node.children.forEach(child => subtreeIds.push(...collectSubtreeIds(child)));
  return subtreeIds;
}

/** Descendientes (en cualquier nivel) de la OU `rootId` dentro del catálogo plano `items`, sin incluir a `rootId` misma. */
export function collectDescendantItems(items: OUItem[], rootId: string): OUItem[] {
  const childrenByParent = new Map<string, OUItem[]>();
  items.forEach(item => {
    if (!item.parentOuId) return;
    const siblings = childrenByParent.get(item.parentOuId) ?? [];
    siblings.push(item);
    childrenByParent.set(item.parentOuId, siblings);
  });

  const descendants: OUItem[] = [];
  const pendingIds = [rootId];
  while (pendingIds.length > 0) {
    const currentId = pendingIds.pop()!;
    const children = childrenByParent.get(currentId) ?? [];
    for (const child of children) {
      descendants.push(child);
      pendingIds.push(child.id);
    }
  }
  return descendants;
}
